import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UploadIcon, X, FileIcon, InfoIcon } from "lucide-react";
import { filledFormService } from "@/features/filledForm/filledFormService";

interface InvoiceFile {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  path?: string;
  file?: File;
  successful?: number;
  failed?: number;
  errorMessage?: string;
}

interface SimplifiedInvoiceFieldProps {
  value: InvoiceFile[];
  onChange: (value: InvoiceFile[]) => void;
  label: string;
  description?: string;
  required?: boolean;
  videoUrl?: string;
  multiline?: boolean;
  processId?: string;
  phaseFormId?: string;
  formFieldId?: string;
}

export const SimplifiedInvoiceField: React.FC<SimplifiedInvoiceFieldProps> = ({
  value = [],
  onChange,
  label,
  description,
  required = false,
  videoUrl,
  multiline = false,
  processId = "",
  phaseFormId = "",
  formFieldId
}) => {
  const [invoiceFiles, setInvoiceFiles] = useState<InvoiceFile[]>(value);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // ID do campo do formulário - usa o fornecido ou extrai do label se não fornecido
  const fieldId = formFieldId || label?.toLowerCase().replace(/\s+/g, '_') || 'invoice_field';

  // Sincronizar o estado local com o valor da prop
  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      setInvoiceFiles(value);
    }
  }, [value]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Se não for multiline, limitar a apenas um arquivo
    if (!multiline && (files.length > 1 || invoiceFiles.length > 0)) {
      // Permitir apenas substituir o arquivo existente
      if (invoiceFiles.length > 0) {
        setInvoiceFiles([]);
      }
      // E usar apenas o primeiro arquivo selecionado
      const filesToProcess = [files[0]];
      processFiles(filesToProcess);
    } else {
      processFiles(Array.from(files));
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const processFiles = async (files: File[]) => {
    // Validar os tipos de arquivo
    const validFiles = files.filter(file => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!multiline) {
        // Apenas XML é permitido
        return fileExtension === 'xml';
      } else {
        // XML, ZIP e RAR são permitidos
        return ['xml', 'zip', 'rar'].includes(fileExtension || '');
      }
    });
    
    if (validFiles.length === 0) {
      alert(multiline 
        ? "Apenas arquivos XML, ZIP e RAR são permitidos." 
        : "Apenas arquivos XML são permitidos.");
      return;
    }
    
    // Criar objetos de upload para os arquivos válidos
    const newInvoiceFiles = validFiles.map(file => ({
      id: Math.random().toString(36).substring(2, 11),
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: "uploading" as const,
      file: file
    }));
    
    // Atualizar estado e notificar mudança para o componente pai
    const updatedInvoiceFiles = multiline 
      ? [...invoiceFiles, ...newInvoiceFiles] 
      : newInvoiceFiles;
    
    setInvoiceFiles(updatedInvoiceFiles);
    onChange(updatedInvoiceFiles);
    
    // Iniciar upload de cada arquivo
    const uploadPromises = newInvoiceFiles.map(async (invoiceFile) => {
      try {
        // Usar o filledFormService com os parâmetros corretos
        const result = await filledFormService.uploadDocument(
          phaseFormId,
          processId,
          fieldId,
          invoiceFile.file as File,
          (progress) => {
            // Atualizar progresso durante upload
            setInvoiceFiles(prev => {
              const updated = prev.map(f =>
                f.id === invoiceFile.id
                  ? { ...f, progress }
                  : f
              );
              onChange(updated);
              return updated;
            });
          }
        );
        
        // Extrair informações adicionais dos dados da resposta
        let successful: number | undefined;
        let failed: number | undefined;
        
        try {
          if (result && result.additionalData) {
            const additionalDataStr = result.additionalData;
            const additionalData = typeof additionalDataStr === 'string'
              ? JSON.parse(additionalDataStr)
              : additionalDataStr;
              
            successful = additionalData?.successful;
            failed = additionalData?.failed;
          }
        } catch (e) {
          console.error("Erro ao analisar additionalData:", e);
        }
        
        // Atualizar status quando upload concluído
        setInvoiceFiles(prev => {
          const updated = prev.map(f =>
            f.id === invoiceFile.id
              ? { 
                  ...f, 
                  status: "completed" as const, 
                  path: result.path, 
                  progress: 100,
                  successful,
                  failed
                }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        return result.path;
      } catch (error) {
        // Atualizar status em caso de erro
        let errorMessage = "Erro ao fazer upload do arquivo";
        let failed = undefined;
        
        // Tentar extrair informações de erro detalhadas quando disponíveis
        try {
          if (error && typeof error === 'object') {
            // @ts-ignore
            const errorData = error.response?.data || error;
            
            // Verificar se é um erro de validação com detalhes sobre notas fiscais
            if (errorData.code === 'ValidationError' && errorData.validationErrors?.invoice) {
              const invoiceError = errorData.validationErrors.invoice;
              errorMessage = invoiceError.message || "Erro na validação das notas fiscais";
              
              if (invoiceError.value && typeof invoiceError.value === 'object') {
                failed = invoiceError.value.failed;
              }
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error("Erro ao processar detalhes do erro:", e);
        }
        
        setInvoiceFiles(prev => {
          const updated = prev.map(f =>
            f.id === invoiceFile.id
              ? { 
                  ...f, 
                  status: "error" as const,
                  errorMessage,
                  failed
                }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        console.error("Erro ao fazer upload do arquivo:", error);
        return null;
      }
    });
    
    await Promise.all(uploadPromises);
  };
  
  const handleRemoveFile = (id: string) => {
    const updatedFiles = invoiceFiles.filter(file => file.id !== id);
    setInvoiceFiles(updatedFiles);
    onChange(updatedFiles);
  };
  
  // Determinar os tipos de arquivo aceitos
  const acceptFileTypes = multiline ? ".xml,.zip,.rar" : ".xml";

  return (
    <div className="w-full">
      <div className="flex items-center mb-2">
        <label className="text-slate-500 font-bold px-2">
          {label}{required && <span className="text-red-500">*</span>}
        </label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger><InfoIcon size={16} className="text-grey-light ml-1" /></TooltipTrigger>
              <TooltipContent>
                <p>{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <div className="space-y-2">
        <p className="text-sm mb-2">
          {multiline 
            ? "Adicione as notas fiscais relacionadas a este processo (XML, ZIP ou RAR)" 
            : "Adicione uma nota fiscal XML relacionada a este processo"}
        </p>
        
        <input
          type="file"
          accept={acceptFileTypes}
          multiple={multiline}
          onChange={handleFileSelect}
          className="hidden"
          ref={fileInputRef}
          id={`invoice-file-${fieldId}`}
        />
        
        <Button
          type="button"
          variant="outline"
          className="w-full justify-center py-6"
          onClick={() => fileInputRef.current?.click()}
          disabled={!multiline && invoiceFiles.length > 0}
        >
          <UploadIcon className="mr-2 h-5 w-5" />
          {multiline ? "Adicionar Arquivos" : "Selecionar Arquivo XML"}
        </Button>
        
        <div className="text-xs text-gray-500 ml-2">
          {multiline 
            ? "Formatos aceitos: XML, ZIP, RAR. Você pode adicionar múltiplos arquivos." 
            : "Formato aceito: XML. Você pode adicionar apenas um arquivo."}
        </div>
        
        {/* Lista de arquivos */}
        {invoiceFiles.length > 0 && (
          <div className="space-y-2 mt-4">
            {invoiceFiles.map((file) => (
              <div 
                key={file.id} 
                className="flex items-start gap-2 p-3 border rounded-md"
              >
                <FileIcon className="h-8 w-8 text-blue-500 mt-1" />
                <div className="flex-1">
                  <p className="font-medium">{file.fileName}</p>
                  <p className="text-xs text-gray-500">
                    Tamanho: {(file.fileSize / 1024).toFixed(1)} KB
                  </p>
                  
                  {/* Status do upload */}
                  <div className="mt-1">
                    {file.status === 'uploading' && (
                      <>
                        <div className="text-xs text-blue-500">
                          Enviando ({Math.round(file.progress)}%)
                        </div>
                        <div className="w-full h-1 bg-gray-200 rounded-full mt-1">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </>
                    )}
                    
                    {file.status === 'completed' && (
                      <div className="text-xs text-green-500">
                        Enviado com sucesso
                        {file.successful !== undefined && (
                          <p className="mt-1">
                            <strong>{file.successful}</strong> notas importadas com sucesso
                            {file.failed !== undefined && file.failed > 0 && 
                             `, <strong>${file.failed}</strong> notas com erro`}.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {file.status === 'error' && (
                      <div className="text-xs text-red-500">
                        {file.errorMessage || "Erro ao processar o arquivo"}
                        {file.failed !== undefined && (
                          <span> (<strong>{file.failed}</strong> notas com erro)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(file.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 